import { Minimize2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PackagingMinimisationAssessmentStoredEntry } from '@/shared/hooks/api/packagingSuppliers'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { PackagingMinimisationAssessmentHistoryTable } from './PackagingMinimisationAssessmentHistoryTable'

type PackagingItemMinimisationTabProps = {
  history: PackagingMinimisationAssessmentStoredEntry[]
  locale: string
  showRunAssessment?: boolean
  onRunAssessmentClick?: () => void
  showPreviewDocument?: boolean
  onPreviewDocumentClick?: () => void
}

function MinimisationTabActions({
  showRunAssessment,
  onRunAssessmentClick,
  showPreviewDocument,
  onPreviewDocumentClick,
  previewDocumentDisabled,
}: {
  showRunAssessment: boolean
  onRunAssessmentClick?: () => void
  showPreviewDocument: boolean
  onPreviewDocumentClick?: () => void
  previewDocumentDisabled: boolean
}) {
  const { t } = useTranslation('packagingSuppliers')
  const showRun = showRunAssessment && onRunAssessmentClick
  const showPreview = showPreviewDocument && onPreviewDocumentClick

  if (!showRun && !showPreview) {
    return null
  }

  return (
    <div className='flex flex-wrap items-center justify-end gap-3'>
      {showPreview && (
        <Button
          type='button'
          variant='outline'
          disabled={previewDocumentDisabled}
          onClick={onPreviewDocumentClick}
        >
          {t('itemDetail.minimisation.document.previewButton')}
        </Button>
      )}
      {showRun && (
        <Button type='button' variant='outline' onClick={onRunAssessmentClick}>
          {t('itemDetail.minimisation.runAssessment')}
        </Button>
      )}
    </div>
  )
}

export function PackagingItemMinimisationTab({
  history,
  locale,
  showRunAssessment = false,
  onRunAssessmentClick,
  showPreviewDocument = false,
  onPreviewDocumentClick,
}: PackagingItemMinimisationTabProps) {
  const { t } = useTranslation('packagingSuppliers')
  const previewDocumentDisabled = history.length === 0
  const showHint = showRunAssessment && onRunAssessmentClick
  const showRun = showRunAssessment && onRunAssessmentClick

  if (history.length === 0) {
    return (
      <div className='space-y-4'>
        {showHint ? (
          <Alert variant='info'>
            <AlertDescription>
              {t('itemDetail.minimisation.runAssessmentHint')}
            </AlertDescription>
          </Alert>
        ) : null}
        <Empty className='rounded-lg border border-dashed py-12'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <Minimize2 className='size-10' />
            </EmptyMedia>
            <EmptyTitle>{t('itemDetail.minimisation.empty')}</EmptyTitle>
          </EmptyHeader>
          {showRun ? (
            <EmptyContent>
              <Button type='button' onClick={onRunAssessmentClick}>
                {t('itemDetail.minimisation.runAssessment')}
              </Button>
            </EmptyContent>
          ) : null}
        </Empty>
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {showHint ? (
        <Alert variant='info'>
          <AlertDescription>
            {t('itemDetail.minimisation.runAssessmentHint')}
          </AlertDescription>
        </Alert>
      ) : null}
      <MinimisationTabActions
        showRunAssessment={showRunAssessment}
        onRunAssessmentClick={onRunAssessmentClick}
        showPreviewDocument={showPreviewDocument}
        onPreviewDocumentClick={onPreviewDocumentClick}
        previewDocumentDisabled={previewDocumentDisabled}
      />
      <PackagingMinimisationAssessmentHistoryTable
        history={history}
        locale={locale}
      />
    </div>
  )
}
